/**
 * Login — the entry. One screen. Stone, word, one button.
 *
 * Strict spec:
 *   • var(--yuo-bg) background, no cards, no borders, no scroll
 *   • 190 px stone (facets + dust + dark smoke halo)
 *   • YUO word underneath (300 weight, 0.56em tracking)
 *   • ONE button at the bottom: Continue with Google (green glow capsule + 18px stone)
 *   • Emerge blur-in on load. Only green accent. No white surfaces anywhere.
 */
import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useResponsive } from "@/lib/responsive";

const KEYFRAMES = `
@keyframes yuo-login-breathe { 0%,100% { opacity:.5 } 50% { opacity:1 } }
@keyframes yuo-login-spin    { to { transform: rotate(360deg) } }
@keyframes yuo-login-drift   { 0%,100% { transform: translate3d(0,0,0) scale(1) } 50% { transform: translate3d(2%,-3%,0) scale(1.06) } }
@keyframes yuo-login-emerge  { from { opacity:0; filter:blur(14px); transform: translateY(14px) scale(.985) } to { opacity:1; filter:blur(0); transform: none } }
@keyframes yuo-login-button-breathe { 0%,100% { filter: brightness(1) } 50% { filter: brightness(1.35) } }
`;

export default function Login() {
  const { signInWithGoogle, user } = useAuth();
  const [busy, setBusy] = useState(false);
  const { isDesktop, isTablet } = useResponsive();

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  if (user) return <Navigate to="/mirror" replace />;

  const onPress = async () => {
    if (busy) return;
    setBusy(true);
    try { await signInWithGoogle(); }
    finally { setBusy(false); }
  };

  const stoneSize = isDesktop ? 190 : isTablet ? 150 : 118;
  const wordSize  = isDesktop ? 30  : isTablet ? 26  : 22;
  const wordTrack = isDesktop ? "0.56em" : isTablet ? "0.5em" : "0.44em";

  return (
    <div
      data-testid="login-screen"
      style={{
        position: "fixed", inset: 0,
        height: "100vh", width: "100vw",
        background: "var(--yuo-bg)",
        color: "var(--yuo-ink)",
        overflow: "hidden",
        fontFamily: '"JetBrains Mono", ui-monospace, monospace',
        display: "flex", flexDirection: "column",
      }}>
      <style>{KEYFRAMES}</style>

      {/* Ambient soft green light — insets from all sides so it never touches edges */}
      <div style={{
        position: "absolute", inset: "4%", pointerEvents: "none",
        background:
          "radial-gradient(30% 24% at 50% 34%, rgba(var(--role-green-rgb),0.10), rgba(var(--role-green-rgb),0.03) 52%, transparent 88%)",
        animation: "yuo-login-drift 34s ease-in-out infinite",
      }} />

      {/* Centre column: stone + YUO */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: 40, position: "relative",
      }}>
        {/* Stone with dark smoke halo */}
        <div style={{
          position: "relative", width: stoneSize, height: stoneSize,
          animation: "yuo-login-emerge 1.4s cubic-bezier(.2,.7,.2,1) both",
        }}>
          {/* Dark smoke — black dust around the stone, no white halo */}
          <div style={{
            position: "absolute", inset: "-210%", borderRadius: "50%", pointerEvents: "none",
            background:
              "radial-gradient(circle, rgba(var(--role-green-rgb),0.09) 0%, rgba(3,6,12,0.55) 34%, rgba(5,6,12,0) 72%)",
          }} />

          {/* Body */}
          <div style={{
            position: "relative", width: "100%", height: "100%", borderRadius: "50%",
            background:
              "radial-gradient(34% 30% at 32% 24%, #FFFFFF 0%, #4DFFA0 13%, #0D3A26 50%, #01040A 100%)",
            boxShadow:
              "0 0 70px rgba(var(--role-green-rgb),0.20)," +
              "0 0 190px 60px rgba(2,4,8,0.92)," +
              "inset -18px -22px 46px rgba(0,0,0,0.92)," +
              "inset 14px 14px 30px rgba(160,255,205,0.26)",
          }}>
            {/* Facets — rotating white wedges via conic-gradient */}
            <div style={{
              position: "absolute", inset: 2, borderRadius: "50%",
              mixBlendMode: "screen",
              animation: "yuo-login-spin 22s linear infinite",
              background:
                "conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.5) 10deg, transparent 24deg," +
                " transparent 118deg, rgba(180,255,220,0.42) 130deg, transparent 146deg," +
                " transparent 248deg, rgba(255,255,255,0.35) 260deg, transparent 274deg)",
            }} />
            {/* Dust — six tiny pulses */}
            <div style={{
              position: "absolute", inset: 0, borderRadius: "50%",
              animation: "yuo-login-breathe 6s ease-in-out infinite",
              background:
                "radial-gradient(2px 2px at 24% 62%, rgba(255,255,255,0.95), transparent)," +
                "radial-gradient(2px 2px at 68% 30%, rgba(255,255,255,0.90), transparent)," +
                "radial-gradient(1.8px 1.8px at 52% 76%, rgba(200,255,225,0.90), transparent)," +
                "radial-gradient(1.6px 1.6px at 78% 58%, rgba(255,255,255,0.80), transparent)," +
                "radial-gradient(1.5px 1.5px at 38% 40%, rgba(255,255,255,0.70), transparent)," +
                "radial-gradient(1.4px 1.4px at 62% 84%, rgba(255,255,255,0.60), transparent)",
            }} />
          </div>
        </div>

        {/* YUO word — 300 weight, wide letter-spacing */}
        <div style={{
          fontSize: wordSize,
          fontWeight: 300,
          letterSpacing: wordTrack,
          paddingLeft: wordTrack,
          color: "var(--fg-82)",
          animation: "yuo-login-emerge 1.4s 0.35s cubic-bezier(.2,.7,.2,1) both",
        }}>
          YUO
        </div>
      </div>

      {/* Bottom — one button + footnote */}
      <div style={{
        flex: "none", display: "flex", flexDirection: "column",
        alignItems: "center", gap: 18, paddingBottom: 56,
        position: "relative",
        animation: "yuo-login-emerge 1.4s 0.70s cubic-bezier(.2,.7,.2,1) both",
      }}>
        <button
          data-testid="login-button"
          onClick={onPress}
          disabled={busy}
          onMouseOver={(e) => { if (!busy) e.currentTarget.style.filter = "brightness(1.3)"; }}
          onMouseOut={(e) => { e.currentTarget.style.filter = "brightness(1)"; }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 14,
            padding: "16px 30px", border: "none", borderRadius: 999, cursor: busy ? "default" : "pointer",
            fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase",
            color: "var(--lit-green-4)", fontFamily: 'inherit',
            background: "radial-gradient(120% 160% at 20% 0%, rgba(var(--role-green-rgb),0.28), rgba(var(--role-green-rgb),0.07))",
            boxShadow: "0 0 34px rgba(var(--role-green-rgb),0.26)",
            animation: busy ? "yuo-login-button-breathe 1.6s ease-in-out infinite" : "none",
            opacity: busy ? 0.9 : 1,
          }}>
          <span style={{
            width: 18, height: 18, borderRadius: "50%",
            background: "radial-gradient(34% 30% at 32% 26%, #FFFFFF, #4DFFA0 20%, #0D3A26 60%, #01040A 100%)",
            boxShadow: "0 0 12px rgba(var(--role-green-rgb),0.7)",
          }} />
          {busy ? "Signing in…" : "Continue with Google"}
        </button>

        <div style={{
          fontSize: 10, letterSpacing: "0.14em",
          color: "var(--fg-3)", textAlign: "center", maxWidth: 420,
        }}>
          By continuing, you agree to our{" "}
          <a href="/terms" style={{ color: "var(--lit-green-3)", textDecoration: "none" }}>Terms</a>
          {" "}and{" "}
          <a href="/privacy" style={{ color: "var(--lit-green-3)", textDecoration: "none" }}>Privacy Policy</a>.
        </div>
      </div>
    </div>
  );
}
